<!-- カテゴリ選択ダイアログ -->
<template>
  <b-modal
    :id="id"
    centered
    :hide-footer="hideFooter"
    content-class="category-content"
    header-class="category-header"
    body-class="category-body"
    :title="title"
    :ok-disabled="!validate"
    :ok-variant="validate ? 'primary' : 'secondary'"
    @show="onShow"
    @ok="onOK"
    @hide="onHide"
  >
    <ul class="category-selector">
      <li
        v-if="withAllCategory"
        class="category-all"
        @click="onClick('all')"
        :class="selected('all')"
      >
        すべてのカテゴリー
      </li>
      <li
        v-for="category in categories"
        :key="category.id"
        @click="onClick(category)"
        :class="selected(category)"
      >
        {{ category.title }}
      </li>
      <li v-if="withCreate" @click="onClick('new')" :class="selected('new')">
        <b-form-input
          v-model="newCategory.title"
          placeholder="新規作成するカテゴリー名"
          :state="newCategoryInputState"
          trim
        />
      </li>
    </ul>
  </b-modal>
</template>

<script lang="ts">
import { BvModalEvent } from "bootstrap-vue";
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeMount,
  onMounted,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { Category } from "../model";
import "./category-modal-dialog.scss";

// カテゴリータイトルの最大文字数
const CATEGORY_TITLE_MAX_LENGTH = 20;

export default defineComponent({
  name: "category-modal-dailog",
  components: {},
  props: {
    id: { type: String, default: () => "" },
    title: { type: String, default: () => "表示するカテゴリー" },
    /** true: フッターを隠す */
    hideFooter: { type: Boolean, default: () => true },
    /** true: 複数選択モード */
    selectMulti: { type: Boolean, default: () => true },
    /** カテゴリーの新規作成UIを出す */
    withCreate: { type: Boolean, default: () => false },
    /** true:「すべてのカテゴリー」選択肢を表示する */
    withAllCategory: { type: Boolean, default: () => true },
    // カテゴリー一覧
    categories: { type: Array as PropType<Category[]>, default: () => [] },
    // フィルタ設定されているカテゴリー一覧
    filter_init: { type: Array as PropType<Category[]>, default: () => [] },
    show: { type: Boolean, default: () => false },
  },
  setup(props, { emit }) {
    // $bvModal は BootstrapVue が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const newCategory = ref<Category>(Category.createTemp(""));
    const filter = ref<Category[]>([]);

    /** true: 新規作成カテゴリーが選択されている */
    const isSelectedNewCategory = (): boolean => {
      return 0 <= filter.value.findIndex((item) => item.id == newCategory.value.id);
    };

    /** OK押して良い状態かどうかを返す */
    const validate = computed((): boolean => {
      if (isSelectedNewCategory()) {
        const len = (newCategory.value.title || "").trim().length;
        return 0 < len && len <= CATEGORY_TITLE_MAX_LENGTH;
      } else {
        return true;
      }
    });

    // 新規カテゴリー入力フォームの state
    const newCategoryInputState = computed((): boolean | undefined => {
      return isSelectedNewCategory() ? validate.value : undefined;
    });

    // 項目の選択状態
    // フィルタが空 → 「すべて」のみON
    // ファイルに何か設定 → 「すべて」OFF、該当項目がON
    const selected = (category: Category | "all" | "new"): { [selector: string]: boolean } => {
      if (filter.value.length === 0) {
        return { selected: category === "all" };
      } else if (category == "all") {
        return { selected: filter.value.length === 0 };
      } else if (category == "new") {
        return { selected: isSelectedNewCategory() };
      } else {
        return { selected: 0 <= filter.value.findIndex((item) => item.id == category.id) };
      }
    };

    const _onClick = (category: Category): void => {
      if (props.selectMulti) {
        // 複数選択有り
        const index = filter.value.findIndex((item) => item.id == category.id);
        if (0 <= index) {
          filter.value.splice(index, 1);
        } else {
          filter.value.push(category);
        }
      } else {
        // 単一選択
        if (0 < filter.value.length) {
          const index = filter.value.findIndex((item) => item.id == category.id);
          filter.value.splice(index, 1);
          filter.value.push(category);
        } else {
          filter.value.push(category);
        }
      }
    };

    // カテゴリーの選択処理
    // 「すべて」が選択された場合、「すべて」のみになる → filter が空
    // その他の項目が選択された場合は、該当項目のON/OFF
    const onClick = (category: Category | "all" | "new"): void => {
      if (category === "all") {
        if (0 === filter.value.length) {
          // 何もしない
        } else {
          filter.value.splice(0, filter.value.length);
        }
        return;
      } else if (category === "new") {
        // カテゴリーの新規作成 項目
        _onClick(newCategory.value);
      } else {
        _onClick(category);
      }
    };

    // ダイアログ表示直前に、背後に残っているテキスト選択を解除する。
    // Android では話題タイトルを選択したままダイアログを開くと、
    // 選択ハイライトと OS のテキスト選択メニューが残り続けるため（DXFLOW-3254）
    const onShow = (): void => {
      window.getSelection()?.removeAllRanges();
    };

    // ✗ボタンで閉じた時
    const onHide = (): void => {
      if (props.hideFooter) {
        // フッター非表示の場合はOKとみなす
        emit("onCloseCategoryFilterDialog", filter.value);
      }
    };

    const onOK = (bvModalEvent: BvModalEvent): void => {
      // 新規作成の場合は、カテゴリー名としてOKかどうかチェックする
      if (validate.value == false) {
        bvModalEvent.preventDefault();
        return;
      }

      emit("onCloseCategoryFilterDialog", filter.value);
    };

    // Lifecycle

    // created 相当（props の一回スナップショット。リアクティブ追跡は不要）
    onBeforeMount(() => {
      filter.value.push(...props.filter_init);
    });

    onMounted(() => {
      if (props.show) {
        vm?.$bvModal.show(props.id);
      }
    });

    return {
      newCategory,
      filter,
      newCategoryInputState,
      validate,
      selected,
      onClick,
      onShow,
      onHide,
      onOK,
    };
  },
});
</script>
